import React, { useMemo, useState } from 'react';
import {
  ArrowRight, BrainCircuit, BriefcaseBusiness, Check, ChevronRight, CircleSlash2,
  Database, Filter, Lightbulb, MessageSquareText, Search, ShieldAlert, Target,
  TrendingUp, Users,
} from 'lucide-react';
import { Avatar, Badge, Button, EmptyState, PageHeader, SectionHeader } from '../components/ui.jsx';
import { getCandidateScore } from '../utils.js';

const VIEWS = [
  { id: 'general', label: '通用优秀人才', icon: Users },
  { id: 'profiles', label: '岗位成功画像', icon: BriefcaseBusiness },
  { id: 'current', label: '当前岗位观察', icon: Target },
];

function GeneralLibrary({ patterns }) {
  const [category, setCategory] = useState('全部');
  const [search, setSearch] = useState('');
  const categories = ['全部', ...new Set(patterns.map((pattern) => pattern.category))];
  const visible = useMemo(() => patterns.filter((pattern) =>
    (category === '全部' || pattern.category === category)
    && (!search || `${pattern.title}${pattern.description}${pattern.positiveSignals.join('')}${pattern.counterSignals.join('')}`
      .toLocaleLowerCase().includes(search.toLocaleLowerCase()))
  ), [patterns, category, search]);

  return (
    <>
      <section className="knowledge-scope-band">
        <span><Users size={20} /></span>
        <div><strong>通用简历优秀人才特征</strong><p>适用于全部岗位，关注结果、学习、系统思考、商业取舍、协作和证据质量，不随当前岗位切换。</p></div>
        <Badge tone="success">组织级标准</Badge>
      </section>

      <div className="pattern-toolbar">
        <div className="segmented-control" aria-label="通用特征分类">{categories.map((item) => <button key={item} className={category === item ? 'active' : ''} aria-pressed={category === item} onClick={() => setCategory(item)}>{item}</button>)}</div>
        <label className="search-field"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索通用特征或证据信号" /></label>
      </div>

      <section className="pattern-grid">
        {visible.map((pattern, index) => (
          <article className="pattern-card" key={pattern.id}>
            <header><span className={`pattern-icon pattern-icon-${index % 4}`}>{index % 3 === 0 ? <TrendingUp size={19} /> : index % 3 === 1 ? <BrainCircuit size={19} /> : <Target size={19} />}</span><div><Badge tone={pattern.category === '潜力信号' ? 'info' : 'success'}>{pattern.category}</Badge><h2>{pattern.title}</h2></div><span className="pattern-confidence"><strong>{Math.round(pattern.confidence * 100)}%</strong><small>基线成熟度</small></span></header>
            <p>{pattern.description}</p>
            <div className="signal-columns">
              <div><strong><Check size={14} />强证据</strong>{pattern.positiveSignals.map((signal) => <span key={signal}>{signal}</span>)}</div>
              <div><strong><CircleSlash2 size={14} />反向信号</strong>{pattern.counterSignals.map((signal) => <span key={signal}>{signal}</span>)}</div>
            </div>
            <footer><span><Database size={14} />{pattern.evidenceCount > 0 ? `${pattern.evidenceCount} 个内部校准样本` : '待积累内部校准样本'}</span><small>{pattern.source}</small></footer>
          </article>
        ))}
      </section>
      {visible.length === 0 && <EmptyState icon={Filter} title="没有匹配的通用特征" />}
    </>
  );
}

function ProfileEvidenceSection({ icon: Icon, title, items, tone = '' }) {
  return (
    <section className={`profile-evidence-section ${tone}`}>
      <h3><Icon size={16} />{title}</h3>
      <ul>{items.map((item) => <li key={item}><i />{item}</li>)}</ul>
    </section>
  );
}

function JobProfileLibrary({ profiles }) {
  const [selectedId, setSelectedId] = useState('profile_dtc_ecommerce_specialist');
  const profile = profiles.find((item) => item.id === selectedId) || profiles[0];

  if (!profile) return <EmptyState icon={BriefcaseBusiness} title="岗位成功画像尚未配置" />;
  return (
    <div className="profile-library-layout">
      <aside className="profile-list-panel">
        <div className="profile-list-heading"><span><BriefcaseBusiness size={18} /></span><div><strong>常见招聘岗位</strong><small>{profiles.length} 个标准画像</small></div></div>
        <div className="profile-list">{profiles.map((item, index) => (
          <button key={item.id} className={profile.id === item.id ? 'active' : ''} aria-pressed={profile.id === item.id} onClick={() => setSelectedId(item.id)}>
            <span className={`profile-number profile-number-${index % 4}`}>{String(index + 1).padStart(2, '0')}</span>
            <div><strong>{item.title}</strong><small>{item.level} · v{item.version}</small></div>
            <ChevronRight size={16} />
          </button>
        ))}</div>
      </aside>

      <section className="profile-detail-panel">
        <header className="profile-detail-head">
          <div><Badge tone="info">岗位成功画像</Badge><h2>{profile.title}</h2><p>{profile.summary}</p></div>
          <div><small>标准来源</small><strong>{profile.source}</strong><span>版本 {profile.version}</span></div>
        </header>

        <section className="profile-focus-section">
          <SectionHeader title="评估重点" description="岗位画像基线；具体招聘仍由 JD、等级、团队缺口和目标覆盖" />
          <div className="profile-focus-grid">{profile.evaluationFocus.map(([label, weight], index) => (
            <div className="profile-focus-row" key={label}><span>{label}</span><div><i style={{ width: `${weight}%` }} className={`focus-tone-${index % 4}`} /></div><strong>{weight}%</strong></div>
          ))}</div>
        </section>

        <div className="profile-evidence-grid">
          <ProfileEvidenceSection icon={Check} title="核心产出" items={profile.coreOutcomes} />
          <ProfileEvidenceSection icon={Database} title="简历 / 作品强证据" items={profile.evidenceSignals} />
          <ProfileEvidenceSection icon={ShieldAlert} title="需要复核的风险信号" items={profile.riskSignals} tone="profile-risk-section" />
          <ProfileEvidenceSection icon={MessageSquareText} title="结构化面试问题" items={profile.interviewQuestions} tone="profile-question-section" />
        </div>
      </section>
    </div>
  );
}

function CurrentJobObservation({ state, navigate }) {
  const job = state?.jobs?.find((item) => item.id === state.selectedJobId) || state?.jobs?.[0];
  const highPotential = [...(state?.candidates || [])]
    .filter((candidate) => !job || candidate.jobId === job.id)
    .filter((candidate) => candidate.potential?.score >= 82)
    .sort((a, b) => b.potential.score - a.potential.score)
    .slice(0, 8);

  return (
    <>
      <section className="current-context-band">
        <div><span><Target size={19} /></span><div><Badge tone="info">仅当前岗位</Badge><h2>{job?.title || '尚未选择岗位'}</h2><p>候选人观察来自当前岗位评分，不会改变组织通用库或其他岗位成功画像。</p></div></div>
        <Button icon={BriefcaseBusiness} onClick={() => navigate('jobs')}>打开岗位配置</Button>
      </section>
      <section className="potential-leaderboard current-potential-panel">
        <SectionHeader title="潜力候选人观察" description="潜力与当前胜任度分开呈现，重点关注学习闭环和职责增长" />
        {highPotential.length > 0 ? <div className="potential-leader-list">
          {highPotential.map((candidate, index) => (
            <button key={candidate.id} onClick={() => navigate('candidate', candidate.id)}>
              <span className="potential-rank">{index + 1}</span><Avatar name={candidate.displayName} tone={index} /><div><strong>{candidate.displayName}</strong><span>{candidate.potential.signals?.join(' · ')}</span></div><div className="current-fit"><small>当前匹配</small><strong>{getCandidateScore(candidate) ?? '—'}</strong></div><div className="potential-fit"><BrainCircuit size={16} /><small>潜力</small><strong>{candidate.potential.score}</strong></div><ArrowRight size={16} />
            </button>
          ))}
        </div> : <EmptyState icon={BrainCircuit} title="当前岗位还没有高潜力候选人" description="候选人完成证据处理后会出现在这里。" />}
      </section>
    </>
  );
}

export default function TalentLibraryPage({ state, navigate }) {
  const [view, setView] = useState('general');
  const patterns = state?.patterns || [];
  const profiles = state?.jobProfiles || [];

  return (
    <div className="page talent-library-page">
      <PageHeader eyebrow="组织人才标准" title="人才标准库" description="通用优秀人才特征、常见岗位成功画像与当前候选人观察分层管理" actions={<Badge tone="info">{profiles.length} 个岗位画像</Badge>} />

      <nav className="knowledge-view-tabs" aria-label="人才标准库视图">
        {VIEWS.map(({ id, label, icon: Icon }) => <button key={id} className={view === id ? 'active' : ''} aria-pressed={view === id} onClick={() => setView(id)}><Icon size={17} /><span>{label}</span>{id === 'profiles' && <Badge tone="neutral">{profiles.length}</Badge>}</button>)}
      </nav>

      {view === 'general' && <GeneralLibrary patterns={patterns} />}
      {view === 'profiles' && <JobProfileLibrary profiles={profiles} />}
      {view === 'current' && <CurrentJobObservation state={state} navigate={navigate} />}

      <section className="learning-method-band"><span><Lightbulb size={21} /></span><div><strong>人才标准是证据框架，不是关键词命中表</strong><p>通用特征适用于所有岗位；岗位画像定义不同工作的成功证据；候选人仍按实际 JD、等级和团队目标独立判断。</p></div></section>
    </div>
  );
}
